/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Brian Bal
 */

import {
    buildFilters,
    filterEventsWithPlaylist,
} from "../../util/playlistStore"
import { trackEvent, SpeedTestJob } from "trux"
import localforage from "localforage"
import { getProperty } from "trux"

let defaultValue = {
    loading: false,
    complete: false,
    error: null,
    value: null,
}

localforage.config({
    name: "trace",
    version: 1.0,
})

export const generateVideoState = (
    eventId,
    playlistId,
    camera,
    allEvents,
    allPlaylists,
    sportType,
    selectedHalf,
    selectedInning,
    selectedBaseballSide,
    selectedBaseballTag,
    selectedCamera,
    selectedOrder
) => {
    eventId = `${eventId}`
    let gameClipId = null
    let eventNumber = null

    if (/^C/gi.test(eventId)) {
        let val = eventId.replace(/^C/gi, "")
        gameClipId = parseInt(val, 10)
    } else {
        let evParts = eventId.split(":")
        eventNumber = evParts[0]
    }

    let curPlaylist = null
    for (let pl of allPlaylists) {
        if (pl.id === playlistId) {
            curPlaylist = pl
            break
        }
    }

    let playlistEvents = []
    if (curPlaylist) {
        if (curPlaylist) {
            playlistEvents = filterEventsWithPlaylist(
                curPlaylist,
                allEvents,
                buildFilters({
                    selectedHalf,
                    selectedInning,
                    selectedBaseballSide,
                    selectedBaseballTag,
                    selectedCamera,
                }),
                selectedOrder
            )
        }
    }

    // find current event/moment
    let curEvent = null
    let curSource = null
    let i = 0
    for (let ev of allEvents) {
        if (eventNumber === "first" && i === 0) {
            curEvent = ev
            break
        }
        if (
            eventNumber &&
            parseInt(ev.event_number, 10) === parseInt(eventNumber, 10)
        ) {
            curEvent = ev
            break
        }
        if (gameClipId && ev.game_clip_id === gameClipId) {
            curEvent = ev
            break
        }
        i++
    }

    // find the current source
    if (curEvent) {
        curSource = curEvent
        if (curEvent.sources) {
            for (let source of curEvent.sources) {
                let sourceCamera = getProperty(
                    "meta.camera.name",
                    source,
                    "none"
                )
                if (sourceCamera === camera) {
                    curSource = source
                    break
                }
            }
        }
    }

    if (curEvent) {
        curEvent.has_spotlight = false
        curEvent.spotlight = null
        curEvent.player = null
    }

    if (!curPlaylist || !curEvent) {
        return {
            eventId: null,
            event: null,
            source: null,
            camera: null,
            playlist: null,
            playlistEvents: [],
        }
    }

    return {
        eventId: curEvent.event_number,
        event: curEvent,
        source: curSource,
        camera: camera,
        playlist: curPlaylist,
        playlistEvents: playlistEvents,
    }
}

const loadEvent = (playlistId, eventId, page) => {
    return (dispatch, getState) => {
        let settings = getState().settings
        let selectedCamera = getState().selectedCamera
        let sportType = getState().sportType
        let selectedHalf = getState().selectedHalf
        let selectedInning = getState().selectedInning
        let selectedBaseballSide = getState().selectedBaseballSide
        let selectedBaseballTag = getState().selectedBaseballTag
        let selectedOrder = getState().selectedOrder
        let gameClipId = null
        let eventNumber = null
        let camera = null
        if (/^C/gi.test(`${eventId}`)) {
            let val = eventId.replace(/^C/gi, "")
            gameClipId = parseInt(val, 10)
        } else {
            let evParts = eventId.split(":")
            eventNumber = parseInt(evParts[0], 10)
            if (evParts.length > 0 && evParts[1]) {
                camera = evParts[1]
            } else {
                camera = "tracecam"
            }
        }

        let videoState = getState().video

        let gameState = getState().game

        if (!gameState.value || !gameState.complete || gameState.error) {
            return { type: "NOOP" }
        }
        let source = getProperty("value.source", videoState, "")
        let sourceCamera = getProperty("meta.camera.name", source, "tracecam")

        // if playlist id, event number, or camera has not changed, don't reload
        if (
            getProperty("value.playlist.id", videoState, "") === playlistId &&
            camera === sourceCamera &&
            (getProperty("value.event.event_number", videoState, "") ===
                eventNumber ||
                getProperty("value.event.game_clip_id", videoState, "") ===
                    gameClipId)
        ) {
            return { type: "NOOP" }
        }
        if (
            playlistId === "uuid" &&
            getProperty("value.playlist.id", videoState, "") === "uuid" &&
            getProperty("value.event.meta.uuid", videoState, "") === eventId
        ) {
            return { type: "NOOP" }
        }

        if (page === "rosters") {
            return
        }

        let currentOrder = selectedOrder
        // conditional order based on sport type and playlist id
        // Sort the filtered events
        if (
            sportType === "baseball" &&
            playlistId.includes("player-highlights")
        ) {
            currentOrder = "baseball-tags"
        }
        if (selectedOrder !== currentOrder) {
            dispatch({
                type: "SELECTED_ORDER",
                selectedOrder: currentOrder,
            })
        }

        const updatedVideoState = generateVideoState(
            eventId,
            playlistId,
            camera,
            gameState.value.events,
            gameState.value.playlists,
            sportType,
            selectedHalf,
            selectedInning,
            selectedBaseballSide,
            selectedBaseballTag,
            selectedCamera,
            currentOrder
        )

        if (
            // JSON.stringify(updatedVideoState) ===
            // JSON.stringify(videoState.value)
            videoState?.value?.event &&
            (eventId === "first" ||
                (getProperty("event.game_clip_id", updatedVideoState, "-") ===
                    getProperty("value.event.game_clip_id", videoState, "--") &&
                    getProperty(
                        "source.game_clip_id",
                        updatedVideoState,
                        "-"
                    ) ===
                        getProperty(
                            "value.source.game_clip_id",
                            videoState,
                            "-"
                        )))
        ) {
            return { type: "NOOP" }
        }

        // Log the network
        if (
            (videoState.value.event &&
                videoState.value.event.event_number !== eventNumber) ||
            videoState.value.event === null
        ) {
            try {
                logNetworkSpeed()
            } catch (e) {
                console.warn("Error calculating network speed")
            }
        }

        dispatch({
            type: "COMMON_SET",
            key: "video",
            value: {
                loading: false,
                complete: true,
                error: null,
                value: updatedVideoState,
            },
            defaultValue: { ...defaultValue },
        })

        // Update rate app moment views
        let views = null
        // eslint-disable-next-line eqeqeq
        if (settings.viewsSinceDialog != null) {
            views = settings.viewsSinceDialog + 1
        }
        // eslint-disable-next-line eqeqeq
        if (views != null) {
            dispatch({
                type: "COMMON_SET",
                key: "settings",
                value: { viewsSinceDialog: views },
            })
            localforage.setItem("rateapp-viewsSinceDialog", views)
        }
    }
}

function logNetworkSpeed() {
    setTimeout(async () => {
        let anAction = "NA"
        const speedTest = new SpeedTestJob(null)
        await speedTest.testSpeed()
        const speed = speedTest.calculate()
        console.log("@@@@@@ Network speed:", speed)
        if (!speed || speed === 0) return
        if (speed <= 1) {
            anAction = "0 -> 1"
        } else if (speed <= 2.5) {
            anAction = "1 -> 2.5"
        } else if (speed <= 5) {
            anAction = "2.5 -> 5"
        } else if (speed <= 7.5) {
            anAction = "5 -> 7.5"
        } else if (speed <= 10) {
            anAction = "7.5 -> 10"
        } else if (speed <= 15) {
            anAction = "10 -> 15"
        } else if (speed <= 25) {
            anAction = "15 -> 25"
        } else if (speed <= 50) {
            anAction = "25 -> 50"
        } else if (speed <= 100) {
            anAction = "50 -> 100"
        } else {
            anAction = "100+"
        }
        trackEvent({
            category: "Speed",
            action: anAction,
            label: "Network Speed",
        })
    }, 3500)
}

export default loadEvent
